import { Instance, SnapshotOut, types } from 'mobx-state-tree';

import { createDept } from '../dept/Dept';
import { createModelCode } from '../model-code/ModelCode';
import { UserGroup } from '../user-group/UserGroup';

import { createSimpleUser } from './SimpleUser';

/**
 * 겸직부서 모델
 */
const ConcurrentDept = types.model('ConcurrentDept').props({
  regDt: types.maybeNull(types.number),
  updDt: types.maybeNull(types.number),
  userDeptRoleSid: types.maybeNull(types.number),
  userSid: types.maybeNull(types.number),
  deptSid: types.number,
  roleTypeCd: types.maybeNull(types.number), // 역할 타입 코드 (302103)
  isPrimary: types.optional(types.boolean, false),
  startDt: types.maybeNull(types.number), // 시작일
  endDt: types.maybeNull(types.number), // 종료일 (null이면 현재 진행중)
  deptNm: types.maybeNull(types.string), // 부서명
  userNm: types.maybeNull(types.string),
  regUser: types.maybeNull(createSimpleUser()),
  updUser: types.maybeNull(createSimpleUser()),
});

/**
 * Model description here for TypeScript hints.
 */

export const UserModel = types
  .model('User')
  .props({
    userSid: types.maybeNull(types.optional(types.number, 0)),
    loginId: types.maybeNull(types.optional(types.string, '')),
    userToken: types.maybeNull(types.optional(types.string, '')),
    userNm: types.optional(types.string, ''),
    acntSttsCd: createModelCode(),
    loginPrvntSttsCd: createModelCode(),

    acntDsabldDt: types.maybeNull(types.string),
    acntExpDt: types.maybeNull(types.string),
    acntLockDt: types.maybeNull(types.string),
    // acntSttsCd: createModelCode(),
    acntttsCdList: types.maybeNull(types.string),
    allUserGroupRoleId: types.maybeNull(types.string),
    beforePwd: types.maybeNull(types.string),
    externalLinkYn: types.optional(types.boolean, false),
    lastLoginDt: types.maybeNull(types.optional(types.number, 0)),
    loginFailCnt: types.maybeNull(types.number),
    // loginId: types.maybeNull(types.optional(types.string, '')),
    // loginPrvntSttsCd: createModelCode(),
    logiTm1sFailDt: types.maybeNull(types.string),
    menuList: types.maybeNull(types.optional(types.array(types.frozen()), [])),
    pwd: types.maybeNull(types.string),
    pwdCorrect: types.maybeNull(types.string),
    lastlyPwd: types.maybeNull(types.string),
    pwdUpdDt: types.maybeNull(types.optional(types.number, 0)),
    // regDt: types.maybeNull(types.optional(types.number, 0)),
    regUser: types.maybeNull(createSimpleUser()),
    sessn: types.maybeNull(types.string),
    svcSttsCd: createModelCode(),
    // updDt: types.maybeNull(types.optional(types.number, 0)),
    updUser: types.maybeNull(createSimpleUser()),
    userGroupSid: types.maybeNull(types.string),
    userGroupList: types.maybeNull(types.optional(types.array(UserGroup), [])),
    userGroupSidList: types.maybeNull(types.optional(types.array(types.number), [])),
    empPosSid: types.maybeNull(types.optional(types.number, 0)), // 직급
    empStatusCd: types.maybeNull(createModelCode()), // 재직상태 (302701: 재직, 302702: 휴직, 302703: 퇴사)
    roleType: types.maybeNull(types.optional(types.string, '')), // 역할 타입 (HEAD, DEPUTY, MEMBER)
    posNm: types.maybeNull(types.optional(types.string, '')), // 직위명
    concurrentDepts: types.maybeNull(types.optional(types.array(ConcurrentDept), [])), // 겸직부서 목록
    concurrentYn: types.maybeNull(types.optional(types.boolean, false)), // 겸직 여부

    whtdDt: types.maybeNull(types.string),
    whtdYn: types.optional(types.boolean, false),

    regDt: types.maybeNull(types.optional(types.number, 0)),
    updDt: types.maybeNull(types.optional(types.number, 0)),

    // deprtCd: types.maybeNull(createModelCode()),
    dept: types.maybeNull(createDept()), // 부서

    // 메일구독 관련
    emailAddr: types.maybeNull(types.string),
    subTime: types.maybeNull(types.number || types.string),
    subDailyYn: types.maybeNull(types.optional(types.boolean, false)),
    subWeeklyYn: types.maybeNull(types.optional(types.boolean, false)),
    subDayWeekNums: types.maybeNull(types.optional(types.array(types.number), [])),
    subMonthlyYn: types.maybeNull(types.optional(types.boolean, false)),
    subDayNums: types.maybeNull(types.optional(types.array(types.number), [])),
  })
  .views((self) => ({}))
  .actions((self) => ({
    resetUser: () => {
      self.userSid = 0;
      self.loginId = '';
      self.userToken = '';
      self.userNm = '';
    },

    setProps: (props: { [key: string]: any }) => {
      const updated = { ...self, ...props } as IUser;
      (Object.keys(updated) as TUserTypeKey[]).forEach((key) => {
        if (typeof self[key] === typeof updated[key]) {
          // @ts-ignore
          self[key] = updated[key];
        } else {
          throw new Error(`${key.toString} Input Type Error`);
        }
      });
    },
  }));

type TUserType = Instance<typeof UserModel>;
type TUserSnapshotType = SnapshotOut<typeof UserModel>;

export interface IUser extends TUserType {}
export type TUserTypeKey = keyof TUserSnapshotType & string;
export interface IUserSnapshot extends TUserSnapshotType {}
export const createUserDefaultModel = () => types.optional(UserModel, {});
